import { useCallback, useEffect, useState } from 'react';
import { apiDelete, apiGet, apiPost } from '../../api/client';
import { Button, Card } from '../../design-system';
import { tokens } from '../../design-system/tokens';

type AccessState = {
  configured: boolean;
  pluginActive: boolean;
  publicId: string | null;
  apiBaseUrl: string | null;
  tokenLast4: string | null;
  tokenCreatedAt: string | null;
  revoked: boolean;
  lastRequestAt: string | null;
};

export function ImpactoIntelligencePanel({ agentId, token }: { agentId: string; token: string | null }) {
  const [access, setAccess] = useState<AccessState | null>(null);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setError('');
    try {
      const data = await apiGet<AccessState>(
        `/api/agents/${agentId}/plugins/impacto-intelligence/access`,
        token,
      );
      setAccess(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer el acceso');
    }
  }, [agentId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function generate() {
    if (!token) return;
    setBusy(true);
    setError('');
    setFreshToken(null);
    try {
      const issued = await apiPost<{ token: string }>(
        `/api/agents/${agentId}/plugins/impacto-intelligence/token`,
        {},
        token,
      );
      setFreshToken(issued.token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo generar el token');
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!token) return;
    setBusy(true);
    setError('');
    setFreshToken(null);
    try {
      await apiDelete(`/api/agents/${agentId}/plugins/impacto-intelligence/token`, token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo revocar el token');
    } finally {
      setBusy(false);
    }
  }

  const base = access?.apiBaseUrl;

  return (
    <Card variant="outlined" padding="md" style={{ marginBottom: 24 }}>
      <h3
        style={{
          margin: 0,
          marginBottom: 8,
          fontSize: tokens.typography.fontSize.base,
          fontWeight: tokens.typography.fontWeight.semibold,
        }}
      >
        API para Impacto Intelligence
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: tokens.colors.neutral[600], lineHeight: 1.5 }}>
        Cada agente activo tiene su propia URL. El token se muestra una sola vez. Con el plugin desactivado la API responde 401.
      </p>
      {!access?.configured && (
        <p style={{ margin: 0, fontSize: 13 }}>Activá el plugin para generar la credencial.</p>
      )}
      {base && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          {['/properties', '/conversations', '/events', '/reservations'].map((path) => (
            <code key={path} style={{ fontSize: 12, wordBreak: 'break-all' }}>
              GET {base}
              {path}
            </code>
          ))}
          <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[600] }}>
            {access?.revoked || !access?.tokenLast4
              ? 'No hay un token vigente.'
              : `Token vigente ····${access.tokenLast4}${access.tokenCreatedAt ? ` · ${new Date(access.tokenCreatedAt).toLocaleString('es-UY')}` : ''}`}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[600] }}>
            {access?.lastRequestAt
              ? `Última llamada ${new Date(access.lastRequestAt).toLocaleString('es-UY')}`
              : 'Todavía no hay llamadas registradas.'}
          </p>
        </div>
      )}
      {freshToken && (
        <p style={{ margin: '0 0 12px', fontSize: 13 }}>
          Token nuevo, copialo ahora:{' '}
          <code style={{ wordBreak: 'break-all' }}>{freshToken}</code>
        </p>
      )}
      {error && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#b91c1c' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="button" onClick={() => void generate()} disabled={busy || !access?.configured}>
          Generar API token
        </Button>
        <Button type="button" variant="secondary" onClick={() => void revoke()} disabled={busy || !access?.tokenLast4}>
          Revocar
        </Button>
      </div>
    </Card>
  );
}
